import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { WhoAmIResponse } from '@carculate/api-client';

import { clearFitterOfflineCache } from '@/lib/fitter-offline';

import { subscribeAuthMessages, type AuthBroadcastMessage } from './authChannel';
import { isFitterPath, loginPathFor } from './loginPath';

/**
 * Story `fe-be-monteur-s5d-offline-en-achtergrond` (S021.5d) deel C — wat een tabblad doet als een
 * ANDER tabblad inlogt of uitlogt (dev-rapport S021.4 §6.9).
 *
 * - `login` → precies één whoami via de BESTAANDE query (`refetchQueries(['auth','whoami'])`,
 *   geen tweede `useQuery`, TC-FE-SHL-10) en NUL `flow/resume` (A-24). De `userId` komt uit die
 *   ververste whoami, nooit uit het bericht (A-23). Op `/monteur…`:
 *     - dezelfde gebruiker → {@link AUTH_TAB_USER_EVENT} (de wachtrij hervat via `setUser`); de
 *       offline-cache blijft;
 *     - een andere gebruiker → query-cache leeg (behalve whoami) + offline-cache leeg (W9) +
 *       {@link AUTH_TAB_USER_EVENT} met de nieuwe gebruiker. De foto's van de vorige blijven
 *       staan en worden niet getoond.
 * - `logout` op `/monteur…` → query-cache + offline-cache leeg (W9) en naar `/monteur/inloggen`;
 *   de wachtrij blijft.
 * - Op een kantoorpad: alleen whoami verversen, geen offline-actie.
 */

/** Window-event naar de monteur-schil: de gebruiker na een `login`-bericht (detail = `{ userId }`). */
export const AUTH_TAB_USER_EVENT = 'carculate:auth-tab-user';

const WHOAMI_KEY = ['auth', 'whoami'] as const;

function isWhoAmIKey(key: readonly unknown[]): boolean {
    return key.length === 2 && key[0] === 'auth' && key[1] === 'whoami';
}

function currentUserId(queryClient: QueryClient): string | null {
    return queryClient.getQueryData<WhoAmIResponse | null>(WHOAMI_KEY)?.userId ?? null;
}

export interface AuthTabSyncDeps {
    queryClient: QueryClient;
    /** Het huidige pad (op het moment van het bericht). */
    pathname: () => string;
    navigate: (to: string) => void;
    /** Window-events (alleen in tests vervangen). */
    dispatch?: (event: Event) => void;
}

/** Verwerkt één bericht. Exporteerd voor tests (DT-TAB). */
export async function handleAuthTabMessage(message: AuthBroadcastMessage, deps: AuthTabSyncDeps): Promise<void> {
    const { queryClient } = deps;
    const onFitter = isFitterPath(deps.pathname());
    const dispatch = deps.dispatch ?? ((e: Event) => window.dispatchEvent(e));

    if (message.type === 'logout') {
        if (!onFitter) {
            // Kantoor: alleen whoami verversen; een 401 daarop volgt het bestaande pad.
            await queryClient.refetchQueries({ queryKey: WHOAMI_KEY, exact: true });
            return;
        }
        queryClient.clear();
        await clearFitterOfflineCache('other-tab');
        deps.navigate(loginPathFor(deps.pathname()));
        return;
    }

    // login
    const before = currentUserId(queryClient);
    await queryClient.refetchQueries({ queryKey: WHOAMI_KEY, exact: true });
    if (!onFitter) return;
    const after = currentUserId(queryClient); // ⚠ uit de ververste whoami, nooit uit het bericht
    if (!after) return;
    if (before !== null && before !== after) {
        queryClient.removeQueries({ predicate: (q) => !isWhoAmIKey(q.queryKey) });
        await clearFitterOfflineCache('other-tab');
    }
    dispatch(new CustomEvent(AUTH_TAB_USER_EVENT, { detail: { userId: after } }));
}

/** Koppelt het kanaal aan deze app (één keer, in `AuthProvider`). */
export function useAuthTabSync(deps: AuthTabSyncDeps): void {
    const depsRef = useRef(deps);
    depsRef.current = deps;
    useEffect(
        () =>
            subscribeAuthMessages((message) => {
                void handleAuthTabMessage(message, depsRef.current).catch(() => {
                    /* een mislukte whoami volgt het bestaande pad (foutstaat of inlogscherm) */
                });
            }),
        [],
    );
}
